import React, { useEffect, useState } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Message } from "primereact/message";

import { InputLabel } from "../../../../components/inputs/InputLabel";
import { ReminderActionResult } from "../../../../hooks/useProspectReminders";
import { formatIsoDate, snoozeBounds, toIsoDate } from "../../../../utils/reminders";

interface SnoozeRemindersDialogProps {
  visible: boolean;
  snoozedUntil: string | null;
  onHide: () => void;
  // A date snoozes, null removes the snooze.
  onSnooze: (until: string | null) => Promise<ReminderActionResult>;
}

/**
 * CWA-189: pause the onboarding reminders until a date. Nothing is sent before
 * it; afterwards the ladder resumes where it stopped and sends no rung twice.
 */
const SnoozeRemindersDialog: React.FC<SnoozeRemindersDialogProps> = ({
  visible,
  snoozedUntil,
  onHide,
  onSnooze,
}) => {
  const [date, setDate] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const { min, max } = snoozeBounds();

  useEffect(() => {
    if (!visible) {
      setDate(null);
      setError(null);
      setSubmitting(false);
    }
  }, [visible]);

  const submit = async (until: string | null) => {
    setSubmitting(true);
    setError(null);
    const result = await onSnooze(until);
    setSubmitting(false);
    if (result.ok) onHide();
    else setError(result.message ?? null);
  };

  const footer = (
    <div>
      <Button label="Cancel" text onClick={onHide} disabled={submitting} />
      {snoozedUntil && (
        <Button
          label="Remove snooze"
          icon="pi pi-bell"
          outlined
          onClick={() => submit(null)}
          disabled={submitting}
        />
      )}
      <Button
        label="Snooze"
        icon="pi pi-clock"
        onClick={() => date && submit(toIsoDate(date))}
        loading={submitting}
        disabled={submitting || !date}
      />
    </div>
  );

  return (
    <Dialog header="Snooze reminders" visible={visible} onHide={onHide} footer={footer} style={{ width: "28rem" }}>
      {error && <Message severity="error" className="w-full mb-3" text={error} />}
      {snoozedUntil && (
        <p className="mt-0">Currently snoozed until {formatIsoDate(snoozedUntil)}.</p>
      )}
      <p className="mt-0">
        No reminder email or Slack post goes out before this date. After it, the reminders carry on where they
        stopped.
      </p>
      <div className="field">
        <InputLabel name="snooze_until" label="Snooze until" />
        <Calendar
          inputId="snooze_until"
          aria-label="Snooze until"
          value={date}
          onChange={(e) => setDate((e.value as Date) ?? null)}
          minDate={min}
          maxDate={max}
          dateFormat="dd/mm/yy"
          showIcon
          className="w-full"
        />
      </div>
    </Dialog>
  );
};

export default SnoozeRemindersDialog;
